import './Admin.scss';
import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/ui/button';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { labels } from '@/locales/en.label';
import { ToggleButton } from 'components/ToggleButton/ToggleButton';
import {
	useGetMigrationsQuery,
	useGetPropBestRevStatusQuery,
	useRunMigrationsMutation,
	useSetPropBestRevStatusMutation,
} from 'features/admin/gql/_gen_/admin.gql';
import {
	useGetControlPanelQuery,
	useSetMaxCustomDateRangeMonthsMutation,
	useSetSupportMessageMutation,
	useToggleAutopilotMutation,
} from 'features/control-panel/gql/_gen_/control-panel.gql';
import { setSupportMessage } from 'features/header/redux/header-slice';
import { useUpdateAutopilotEnabledMutation } from 'features/property/gql/_gen_/autopilot_enabled.gql';
import { selectProperty } from 'features/property/redux/selectors';
import { useApolloError } from 'hooks/useApolloError';
import {
	AlertTriangle,
	ShieldCheck,
	ShieldOff,
	TriangleAlert,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { FaPlayCircle } from 'react-icons/fa';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { AdminUserTable } from './components/admin-user-table';
import { LLMEvaluations } from './components/llm-evaluations';
import { LLMTraces } from './components/llm-traces';

export function Admin() {
	const history = useHistory();
	const location = useLocation();
	const [autopilotStatus, setAutopilotStatus] = useState(false);
	const [autopilotHotelStatus, setAutopilotHotelStatus] = useState(false);
	const [bestRevStatus, setBestRevStatus] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [supportMessage, setMessage] = useState('');
	const [maxDateRangeMonths, setMaxDateRangeMonths] = useState('');
	const [maxDateRangeError, setMaxDateRangeError] = useState('');

	// Parse current URL search parameters
	const searchParams = new URLSearchParams(location.search);

	// Get active tab from URL, default to 'system'
	const activeTab =
		(searchParams.get('tab') as 'system' | 'ai-tracer' | 'users') || 'system';

	// Function to update tab in URL
	const setActiveTab = (tab: 'system' | 'ai-tracer' | 'users') => {
		const newParams = new URLSearchParams(location.search);
		newParams.set('tab', tab);

		if (tab === 'ai-tracer') {
			// If switching to ai-tracer and no sub-tab is set, default to traces
			if (!newParams.has('sub-tab')) {
				newParams.set('sub-tab', 'traces');
			}
		} else {
			// If switching away from ai-tracer, remove the sub-tab parameter
			newParams.delete('sub-tab');
		}

		history.push({
			pathname: location.pathname,
			search: newParams.toString(),
		});
	};

	// Get AI tracer sub-tab from URL, default to 'traces'
	const aiTracerSubTab =
		(searchParams.get('sub-tab') as 'traces' | 'evaluations') || 'traces';

	// Function to update AI tracer sub-tab in URL
	const setAiTracerSubTab = (subTab: 'traces' | 'evaluations') => {
		const newParams = new URLSearchParams(location.search);
		newParams.set('sub-tab', subTab);
		history.push({
			pathname: location.pathname,
			search: newParams.toString(),
		});
	};

	const property = useAppSelector(selectProperty);
	const propertyId = property.propertyId;
	const [autopilotSwitchLoading, setAutopilotSwitchLoading] = useState(true);

	const dispatch = useAppDispatch();
	const { handleApolloError } = useApolloError();

	const { data: controlPanelData, loading: loadingControlPanel } =
		useGetControlPanelQuery();

	const supportMessageData = controlPanelData?.getControlPanel?.support_message;
	const saveButtonDisplayed = !supportMessageData
		? !!supportMessage.length
		: supportMessage !== supportMessageData;

	const maxDateRangeMonthsData =
		controlPanelData?.getControlPanel?.max_custom_date_range_months;
	const maxDateRangeSaveDisplayed =
		String(maxDateRangeMonthsData ?? '') !== maxDateRangeMonths;

	const messageBody = () => {
		return (
			<>
				<div className='tw-space-y-3 tw-py-2 tw-text-sm tw-text-card-foreground'>
					<div className='tw-font-medium tw-text-gray dark:tw-text-gray'>
						You are about to disable Autopilot for the entire application. This
						action will:
					</div>
					<div className='tw-rounded-md  tw-p-4 tw-border tw-bg-card tw-text-card-foreground tw-mt-2 tw-shadow-inner'>
						<ul className='tw-list-disc tw-list-inside tw-text-sm mt-2 tw-space-y-1.5'>
							<li>
								Immediately stop all Autopilot functionality across all systems
							</li>
							<li>Affect all users currently using the application</li>
							<li>Disable all Autopilot-dependent features until re-enabled</li>
						</ul>
					</div>
					<div className='tw-mt-2 tw-font-medium tw-text-destructive/100 dark:tw-text-destructive/100'>
						Are you absolutely sure you want to proceed?
					</div>
				</div>
			</>
		);
	};

	useEffect(() => {
		if (controlPanelData?.getControlPanel) {
			if (controlPanelData.getControlPanel.allow_autopilot) {
				setAutopilotStatus(true);
			} else {
				setAutopilotStatus(false);
			}
			if (controlPanelData.getControlPanel.support_message) {
				setMessage(controlPanelData.getControlPanel.support_message);
			}
			if (
				typeof controlPanelData.getControlPanel.max_custom_date_range_months ===
				'number'
			) {
				setMaxDateRangeMonths(
					String(controlPanelData.getControlPanel.max_custom_date_range_months),
				);
			}
			setAutopilotSwitchLoading(loadingControlPanel);
		}
	}, [controlPanelData, loadingControlPanel]);

	const [toggleAutopilot] = useToggleAutopilotMutation({
		refetchQueries: ['GetControlPanel'],
		onCompleted: () => {
			setAutopilotSwitchLoading(false);
		},
		onError: (error) => {
			if (
				typeof controlPanelData?.getControlPanel?.allow_autopilot === 'boolean'
			) {
				setAutopilotStatus(controlPanelData?.getControlPanel?.allow_autopilot);
			}
			handleApolloError(error);
		},
	});

	const [saveSupportMessage] = useSetSupportMessageMutation({
		refetchQueries: ['GetControlPanel'],
		variables: {
			message: supportMessage,
		},
		onCompleted: (data) => {
			if (data?.setSupportMessage?.support_message) {
				dispatch(setSupportMessage(data.setSupportMessage.support_message));
			}
		},
		onError: (error) => {
			handleApolloError(error);
		},
	});

	const [saveMaxDateRangeMonths] = useSetMaxCustomDateRangeMonthsMutation({
		refetchQueries: ['GetControlPanel'],
		onError: (error) => {
			handleApolloError(error);
		},
	});

	const { data: bestRevStatusData, loading: loadingBestRevStatus } =
		useGetPropBestRevStatusQuery({
			variables: {
				propertyId,
			},
			fetchPolicy: 'network-only',
		});

	const [setBestRevAutopilotEnabled] = useUpdateAutopilotEnabledMutation({
		refetchQueries: ['GetPropBestRevStatus'],
		variables: {
			propertyId,
			autopilotEnabled: autopilotHotelStatus,
		},
		onError: (error) => {
			if (
				typeof bestRevStatusData?.getPropertyByPropertyId?.autopilot_enabled ===
				'boolean'
			) {
				setAutopilotHotelStatus(
					bestRevStatusData?.getPropertyByPropertyId?.autopilot_enabled,
				);
			}
			handleApolloError(error);
		},
	});

	const [setBestRevActive] = useSetPropBestRevStatusMutation({
		onError: (error) => {
			if (
				typeof bestRevStatusData?.getPropertyByPropertyId?.bestrev_active ===
				'boolean'
			) {
				setBestRevStatus(
					bestRevStatusData?.getPropertyByPropertyId?.bestrev_active,
				);
			}
			handleApolloError(error);
		},
	});

	useEffect(() => {
		if (
			typeof bestRevStatusData?.getPropertyByPropertyId?.bestrev_active ===
			'boolean'
		) {
			setBestRevStatus(
				bestRevStatusData?.getPropertyByPropertyId?.bestrev_active,
			);
		}
		if (
			typeof bestRevStatusData?.getPropertyByPropertyId?.autopilot_enabled ===
			'boolean'
		) {
			setAutopilotHotelStatus(
				bestRevStatusData?.getPropertyByPropertyId?.autopilot_enabled,
			);
		}
	}, [bestRevStatusData]);

	const { data: migrationsData } = useGetMigrationsQuery();

	const [runMigrations, { loading: migrationLoading }] =
		useRunMigrationsMutation({
			refetchQueries: ['GetMigrations'],
		});

	const unexecutedMigrations = migrationsData?.getMigrations?.filter(
		(m) => !m?.executedAt,
	);

	const migrationNames = unexecutedMigrations?.map((x) => {
		return x?.name?.split('_').slice(1).join('_');
	});

	const handleAutopilotToggle = () => {
		setAutopilotStatus((prev) => !prev);
		setAutopilotSwitchLoading(true);
		toggleAutopilot();
		setShowConfirmation(false);
	};

	const handleSwitchChange = () => {
		if (autopilotStatus) {
			setShowConfirmation(true); // If trying to turn off Autopilot, show confirmation dialog
		} else {
			setAutopilotSwitchLoading(true);
			toggleAutopilot();
			setAutopilotStatus(true); // If turning on Autopilot, no confirmation needed
		}
	};

	const handleAutopilotHotelToggle = () => {
		setBestRevAutopilotEnabled({
			refetchQueries: ['GetPropBestRevStatus'],
			variables: {
				propertyId,
				autopilotEnabled: !autopilotHotelStatus,
			},
		});
		setAutopilotHotelStatus((prev) => !prev);
	};

	const handleBestRevToggle = () => {
		setBestRevActive({
			refetchQueries: ['GetPropBestRevStatus'],
			variables: {
				propertyId,
				status: !bestRevStatus,
			},
		});
		setBestRevStatus((prev) => !prev);
	};

	const handleSupportMessage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setMessage(event.target.value);
	};

	const handleSaveSupportMessage = () => {
		saveSupportMessage({
			refetchQueries: ['GetControlPanel'],
			variables: {
				message: supportMessage,
			},
		});
	};

	const handleMaxDateRangeMonths = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setMaxDateRangeMonths(event.target.value);
		const parsed = Number(event.target.value);
		if (
			!event.target.value ||
			!Number.isInteger(parsed) ||
			parsed < 1 ||
			parsed > 60
		) {
			setMaxDateRangeError(
				labels.admin.max_date_range_form.form_error.invalid_value,
			);
		} else {
			setMaxDateRangeError('');
		}
	};

	const handleSaveMaxDateRangeMonths = () => {
		const parsed = Number(maxDateRangeMonths);
		if (!Number.isInteger(parsed) || parsed < 1 || parsed > 60) {
			setMaxDateRangeError(
				labels.admin.max_date_range_form.form_error.invalid_value,
			);
			return;
		}
		saveMaxDateRangeMonths({
			refetchQueries: ['GetControlPanel'],
			variables: {
				months: parsed,
			},
		});
	};

	const clearSupportMessage = () => {
		setMessage('');
		saveSupportMessage({
			refetchQueries: ['GetControlPanel'],
			variables: {
				message: '',
			},
		});
		dispatch(setSupportMessage(''));
	};

	const singularMessage = 'The following migration has not been run';
	const pluralMessage = `The following ${unexecutedMigrations?.length} migrations have not been run. This may result in database errors`;

	return (
		<div className='admin-container tw-flex tw-flex-col tw-space-y-6'>
			<div className='tw-mb-4'>
				<h1 className='tw-text-2xl tw-font-bold tw-mb-2'>Admin Dashboard</h1>
				<p className='tw-text-muted-foreground'>
					Manage system settings, monitor AI performance, and oversee user
					administration
				</p>
			</div>

			<Tabs
				value={activeTab}
				onValueChange={(value: string) =>
					setActiveTab(value as 'system' | 'ai-tracer' | 'users')
				}
			>
				<TabsList className='tw-grid tw-w-full tw-grid-cols-3 tw-max-w-md'>
					<TabsTrigger value='system'>System</TabsTrigger>
					<TabsTrigger value='ai-tracer'>AI Tracer</TabsTrigger>
					<TabsTrigger value='users'>Users</TabsTrigger>
				</TabsList>

				<TabsContent value='system' className='tw-mt-6 tw-space-y-6'>
					{/* App Autopilot switch */}
					<div className='tw-grid tw-gap-6 md:tw-grid-cols-2 lg:tw-grid-cols-3'>
						<Card className='tw-border-2'>
							<CardHeader>
								<div className='tw-flex tw-items-center tw-justify-between'>
									<CardTitle className='tw-flex tw-items-center tw-gap-2 tw-text-xl'>
										{autopilotSwitchLoading ? (
											<Skeleton className='tw-h-6 tw-w-12 tw-rounded-full' />
										) : autopilotStatus ? (
											<ShieldCheck className='tw-h-6 tw-w-6 tw-text-primary' />
										) : (
											<ShieldOff className='tw-h-6 tw-w-6 tw-text-destructive/100' />
										)}
										{labels.admin.autopilot_switch.header}
									</CardTitle>
									{autopilotSwitchLoading ? (
										<Skeleton className='tw-h-6 tw-w-12 tw-rounded-full' />
									) : (
										<Switch
											checked={autopilotStatus}
											onCheckedChange={handleSwitchChange}
											className={
												autopilotStatus
													? 'data-[state=checked]:tw-bg-primary'
													: 'data-[state=unchecked]:tw-bg-destructive/100'
											}
										/>
									)}
								</div>
								<CardDescription>
									{labels.admin.autopilot_switch.description}
								</CardDescription>
							</CardHeader>
							<CardContent className='tw-pt-2'>
								{autopilotSwitchLoading ? (
									<div className='tw-space-y-4'>
										<Skeleton className='tw-h-20 tw-w-full tw-rounded-md' />
									</div>
								) : (
									<div
										className={`tw-rounded-md tw-p-4 ${
											autopilotStatus
												? 'tw-bg-secondary tw-text-secondary-foreground'
												: 'tw-bg-destructive/20 tw-text-destructive/100 dark:tw-bg-destructive/20 dark:tw-text-destructive/100'
										} tw-flex tw-items-start tw-gap-3 tw-shadow-inner tw-transition-all tw-duration-300`}
									>
										<AlertTriangle className='tw-h-5 tw-w-5 tw-mt-0.5 tw-lex-shrink-0' />
										<div>
											<p className='tw-font-semibold'>
												{autopilotStatus
													? `${labels.admin.autopilot_switch.autopilot_enabled}`
													: `${labels.admin.autopilot_switch.autopilot_disabled}`}
											</p>
											<p className='tw-text-sm tw-mt-1 tw-opacity-90'>
												{autopilotStatus
													? `${labels.admin.autopilot_switch.autopilot_enabled_info}`
													: `${labels.admin.autopilot_switch.warning}`}
											</p>
										</div>
									</div>
								)}
							</CardContent>
						</Card>
					</div>

					<div className='tw-rounded-xl tw-border tw-bg-card tw-shadow tw-p-6'>
						<div className='tw-space-y-6'>
							<div className='admin-field'>
								<Label htmlFor='autopilot-hotel'>
									{
										labels.admin.support_message_form.switch
											.autopilot_for_property
									}
									<span className='tw-font-bold tw-text-destructive'>
										{propertyId}
									</span>
								</Label>
								<div className='bestrev-toggle'>
									{loadingControlPanel ? (
										<Skeleton className='tw-h-6 tw-w-12 tw-rounded-full' />
									) : (
										<ToggleButton
											id='autopilot-hotel'
											selected={autopilotHotelStatus}
											labelOptions={['Off', 'On']}
											toggleSelected={handleAutopilotHotelToggle}
										/>
									)}
								</div>
							</div>

							<div className='admin-field'>
								<Label htmlFor='bestrev'>
									{
										labels.admin.support_message_form.switch
											.bestrev_for_property
									}
									<span className='tw-font-bold tw-text-destructive'>
										{propertyId}
									</span>
								</Label>
								<div className='bestrev-toggle'>
									{loadingBestRevStatus ? (
										<Skeleton className='tw-h-6 tw-w-12 tw-rounded-full' />
									) : (
										<ToggleButton
											id='bestrev'
											selected={bestRevStatus}
											labelOptions={['Off', 'On']}
											toggleSelected={handleBestRevToggle}
										/>
									)}
								</div>
							</div>

							{loadingControlPanel ? (
								<Skeleton className='tw-h-20 tw-w-1/2 tw-rounded-md' />
							) : (
								<div className='admin-field'>
									<Label htmlFor='supportMessage'>
										{labels.admin.support_message_form.label}
									</Label>
									<input
										id='supportMessage'
										type='text'
										className='support-message-input'
										value={supportMessage}
										onChange={handleSupportMessage}
									/>
									{saveButtonDisplayed && (
										<Button
											type='button'
											className='save'
											onClick={() => handleSaveSupportMessage()}
										>
											{labels.admin.support_message_form.btn.save}
										</Button>
									)}
									{controlPanelData?.getControlPanel?.support_message && (
										<Button
											type='button'
											className='clear'
											onClick={clearSupportMessage}
										>
											{labels.admin.support_message_form.btn.clear_message}
										</Button>
									)}
								</div>
							)}

							{loadingControlPanel ? (
								<Skeleton className='tw-h-20 tw-w-1/2 tw-rounded-md' />
							) : (
								<div className='admin-field'>
									<Label htmlFor='maxDateRangeMonths'>
										{labels.admin.max_date_range_form.label}
									</Label>
									<input
										id='maxDateRangeMonths'
										type='number'
										min={1}
										max={60}
										className='support-message-input'
										value={maxDateRangeMonths}
										onChange={handleMaxDateRangeMonths}
									/>
									{maxDateRangeError && <p>{maxDateRangeError}</p>}
									{maxDateRangeSaveDisplayed && !maxDateRangeError && (
										<Button
											type='button'
											className='save'
											onClick={handleSaveMaxDateRangeMonths}
										>
											{labels.admin.max_date_range_form.btn.save}
										</Button>
									)}
								</div>
							)}

							{!!unexecutedMigrations?.length &&
								process.env.NODE_ENV !== 'production' && (
									<div className='migrations'>
										<p>
											{unexecutedMigrations?.length > 1
												? pluralMessage
												: singularMessage}
											. This may result in database errors.
										</p>
										<ul>
											{migrationNames?.map((migration) => (
												<li key={migration}>{migration}</li>
											))}
										</ul>
										<Button
											type='button'
											onClick={() => runMigrations()}
											className='migrations-button'
											disabled={migrationLoading}
										>
											<span>Run Migrations</span>
											{migrationLoading ? (
												<AiOutlineLoading3Quarters
													style={{
														animation: 'spin 1s linear infinite',
													}}
												/>
											) : (
												<FaPlayCircle />
											)}
										</Button>
									</div>
								)}

							<div className='links'>
								<Link to={'/help/create'}>
									<Button type='button'>
										{labels.admin.support_message_form.btn.submit}
									</Button>
								</Link>
								<Link to={'/admin/video-uploader'}>
									<Button type='button'>
										{labels.admin.support_message_form.btn.revBot}
									</Button>
								</Link>
							</div>
						</div>
					</div>
				</TabsContent>

				<TabsContent value='ai-tracer' className='tw-mt-6'>
					<Card className='tw-shadow-sm'>
						<CardHeader>
							<CardTitle className='tw-flex tw-items-center tw-gap-2'>
								AI Tracer Dashboard
							</CardTitle>
							<CardDescription>
								Monitor AI model performance, traces, and evaluations for
								BestREV
							</CardDescription>
						</CardHeader>
						<CardContent>
							<Tabs
								value={aiTracerSubTab}
								onValueChange={(value: string) =>
									setAiTracerSubTab(value as 'traces' | 'evaluations')
								}
								className='tw-w-full'
							>
								<TabsList className='tw-grid tw-w-full tw-grid-cols-2 tw-max-w-md'>
									<TabsTrigger value='traces'>LLM Traces</TabsTrigger>
									<TabsTrigger value='evaluations'>LLM Evaluations</TabsTrigger>
								</TabsList>
								<TabsContent value='traces' className='tw-mt-4'>
									<LLMTraces environmentFilter='bestrev' />
								</TabsContent>
								<TabsContent value='evaluations' className='tw-mt-4'>
									<LLMEvaluations environmentFilter='bestrev' />
								</TabsContent>
							</Tabs>
						</CardContent>
					</Card>
				</TabsContent>

				<TabsContent value='users' className='tw-mt-6'>
					<Card className='tw-shadow-sm'>
						<CardHeader>
							<CardTitle>User Management</CardTitle>
							<CardDescription>
								Manage user accounts and permissions
							</CardDescription>
						</CardHeader>
						<CardContent>
							<AdminUserTable />
						</CardContent>
					</Card>
				</TabsContent>
			</Tabs>
			<Modal
				isShown={showConfirmation}
				isConfirmationModal={true}
				hide={() => setShowConfirmation(false)}
				headerText={'Warning: Disabling Autopilot System-wide'}
				headerClassName={'tw-text-destructive/2100 flex items-center gap-2'}
				onConfirm={handleAutopilotToggle}
				onCancel={() => setShowConfirmation(false)}
				messageBody={messageBody()}
				hasIcon={true}
				icon={<TriangleAlert color='red' />}
				confirmBtnText={'Yes, Disable Autopilot'}
				confirmBtnClassName={
					'tw-bg-destructive/20 hover:tw-bg-destructive/20 hover:tw-text-white'
				}
				cancelBtnText={'Cancel'}
			/>
		</div>
	);
}
